import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

/** specs/changes/authored-skills/openapi.yaml (ADR-216). */
export interface SkillSummary {
  name: string;
  description: string;
  latestVersion: number;
  spaceId: string;
  createdAt: string;
  updatedAt: string;
  publishedBy?: string;
}

export interface SkillFileMeta {
  path: string;
  bytes: number;
  sha256: string;
}

export interface SkillVersionSummary {
  name: string;
  version: number;
  description: string;
  publishedAt: string;
  publishedBy?: string;
  /** The version's change note; '' when none was given (Amendment 1). */
  note?: string;
  fileCount: number;
  totalBytes: number;
  files: SkillFileMeta[];
}

export interface SkillDetail extends SkillSummary {
  versions: SkillVersionSummary[];
}

export interface SkillVersionFiles {
  name: string;
  version: number;
  description: string;
  publishedAt: string;
  publishedBy?: string;
  files: (SkillFileMeta & { content: string })[];
}

const path = (name: string) => `/standards/skills/${encodeURIComponent(name)}`;

/** The refusal a publish gets when the skill moved on since its baseVersion. */
export const STALE_BASE = 'SYD-K017';

export interface PublishInput {
  files: { path: string; content: string }[];
  note?: string;
  /** The version the edit started from; a newer latest refuses the publish (409 SYD-K017). */
  baseVersion: number;
}

export const skillsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async list(): Promise<SkillSummary[]> {
    const { data } = await api.get('/standards/skills');
    return data.skills;
  },

  async get(name: string): Promise<SkillDetail> {
    const { data } = await api.get(path(name));
    return data;
  },

  async version(name: string, version: number | 'latest' = 'latest'): Promise<SkillVersionFiles> {
    const { data } = await api.get(`${path(name)}/versions/${version}`);
    return data;
  },

  /** Publish an edit as the next version (ADR-216 Amendment 1). */
  async publish(name: string, input: PublishInput): Promise<SkillVersionSummary> {
    const { data } = await api.post(`${path(name)}/versions`, {
      files: input.files,
      baseVersion: input.baseVersion,
      ...(input.note?.trim() ? { note: input.note.trim() } : {}),
    });
    return data;
  },

  async remove(name: string): Promise<void> {
    await api.delete(path(name));
  },
};
